import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Plus, Pencil, Trash2, UserCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const PERMS = [
  { key: "registrar_movimientos", label: "Registrar movimientos" },
  { key: "gestionar_flota", label: "Gestionar camiones y conductores" },
  { key: "ver_reportes", label: "Ver y exportar reportes" },
];

const EMPTY = { name: "", email: "", password: "", role: "operativo", permissions: ["registrar_movimientos", "gestionar_flota", "ver_reportes"], active: true };

export default function Admin() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const { data: users = [] } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, permissions: u.permissions || [], active: u.active !== false });
    setOpen(true);
  };

  const togglePerm = (key) =>
    setForm((f) => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, permissions: form.permissions, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/admin/users", form);
        toast.success("Usuario creado");
      }
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar al usuario ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Usuario eliminado");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-cyan-600" /> Panel del Administrador
          </h1>
          <p className="text-sm text-slate-500">Gestión de usuarios, roles y permisos</p>
        </div>
        <Button onClick={openNew} className="bg-navy hover:bg-navy-800 text-white" data-testid="new-user-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-navy hover:bg-navy">
                <TableHead className="text-white">Usuario</TableHead>
                <TableHead className="text-white">Rol</TableHead>
                <TableHead className="text-white">Permisos</TableHead>
                <TableHead className="text-white">Estado</TableHead>
                <TableHead className="text-white text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell>
                    <p className="font-semibold text-navy">{u.name}</p>
                    <p className="text-xs text-slate-400">{u.email}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={u.role === "admin" ? "bg-navy text-telemetry border-navy" : u.role === "tv" ? "bg-slate-800 text-white border-slate-800" : "bg-cyan-50 text-cyan-800 border-cyan-300"}>
                      {u.role === "admin" ? "Administrador" : u.role === "tv" ? "Solo Vista TV" : "Operativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-52">
                    {u.role === "admin" ? "Todos los permisos" : u.role === "tv" ? "Solo visualización de la Vista TV" : (u.permissions || []).map((p) => PERMS.find((x) => x.key === p)?.label || p).join(", ") || "Sin permisos"}
                  </TableCell>
                  <TableCell>
                    <span className={`flex items-center gap-1.5 text-xs font-semibold ${u.active !== false ? "text-emerald-600" : "text-red-500"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.active !== false ? "bg-emerald-500" : "bg-red-500"}`} />
                      {u.active !== false ? "Activo" : "Inactivo"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(u)} className="p-2 text-slate-400 hover:text-cyan-600" data-testid={`edit-user-${u.email}`}><Pencil className="w-4 h-4" /></button>
                    {u.id !== me?.id && (
                      <button onClick={() => remove(u)} className="p-2 text-slate-400 hover:text-red-600" data-testid={`delete-user-${u.email}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ShipsgoSettings />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2"><UserCheck className="w-5 h-5 text-cyan-600" /> {editing ? "Editar Usuario" : "Crear Usuario"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="u-name">Nombre</Label>
                <Input id="u-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="u-role">Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operativo" data-testid="role-operativo">Operativo</SelectItem>
                    <SelectItem value="admin" data-testid="role-admin">Administrador</SelectItem>
                    <SelectItem value="tv" data-testid="role-tv">Solo Vista TV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-email">Correo electrónico</Label>
              <Input id="u-email" type="email" required disabled={!!editing} value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-password">{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</Label>
              <Input id="u-password" type="password" required={!editing} value={form.password}
                     onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
            </div>
            {form.role === "operativo" && (
              <div className="space-y-2" data-testid="permissions-group">
                <Label>Permisos del operativo</Label>
                {PERMS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.permissions.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} />
                    <span className="text-sm text-slate-600">{p.label}</span>
                  </label>
                ))}
              </div>
            )}
            {editing && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="user-active-checkbox" />
                <span className="text-sm text-slate-600">Usuario activo</span>
              </label>
            )}
            <Button type="submit" disabled={saving} className="w-full bg-navy hover:bg-navy-800 text-white h-11" data-testid="user-save-button">
              {saving ? "Guardando..." : editing ? "Guardar Cambios" : "Crear Usuario"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShipsgoSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!key.trim()) return;
    setSaving(true);
    try {
      await api.put("/settings", { shipsgo_api_key: key.trim() });
      toast.success("API Key de ShipsGo guardada");
      setKey("");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="settings-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-lg flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-cyan-600" /> Configuración · Integración ShipsGo
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-60 space-y-1.5">
          <Label htmlFor="shipsgo-key">API Key de ShipsGo</Label>
          <Input id="shipsgo-key" type="password" value={key} onChange={(e) => setKey(e.target.value)}
                 placeholder={data?.shipsgo_configured ? `Configurada (${data.shipsgo_api_key_masked})` : "Pegue su API Key de ShipsGo"}
                 data-testid="shipsgo-key-input" autoComplete="off" />
        </div>
        <Button onClick={save} disabled={saving || !key.trim()} className="bg-navy hover:bg-navy-800 text-white" data-testid="shipsgo-key-save">
          {saving ? "Guardando..." : "Guardar"}
        </Button>
        <Badge variant="outline" className={data?.shipsgo_configured ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-300"}
               data-testid="shipsgo-status-badge">
          {data?.shipsgo_configured ? "Configurada" : "Sin configurar"}
        </Badge>
      </CardContent>
    </Card>
  );
}
